import axios from 'axios';
import { API_BASE } from '../utils/apiBase';

const BASE_URL = API_BASE;

const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

export function setAuthToken(token) {
  if (token) {
    // Strip leading "Bearer " if caller already prefixed it — prevents double-prefix
    const raw = token.startsWith('Bearer ') ? token.slice(7) : token;
    api.defaults.headers.common.Authorization = `Bearer ${raw}`;
  } else {
    delete api.defaults.headers.common.Authorization;
  }
}

// Initialize from localStorage at module load so any component that imports api
// and immediately makes requests gets the correct Authorization header — no
// dependency on a React useEffect firing first.
(function initFromStorage() {
  const stored = localStorage.getItem('authToken');
  if (stored) setAuthToken(stored);
}());

// View As (batch G, G2): while a super admin views the app as another user,
// every request carries that user's read-only token instead of the admin's,
// and no tenant or firm override. Only stopping View As uses the admin token.
export const VIEW_AS_TOKEN_KEY = 'viewAsToken';
export const VIEW_AS_USER_KEY  = 'viewAsUser';
export function clearViewAs() {
  sessionStorage.removeItem(VIEW_AS_TOKEN_KEY);
  sessionStorage.removeItem(VIEW_AS_USER_KEY);
}

// Attach auth token + tenant/firm context on every request.
// Auth endpoints (/auth/login, /auth/register, /auth/refresh) are skipped —
// they're public and sending a stale token there creates retry-loop edge cases.
api.interceptors.request.use(config => {
  config.headers = config.headers || {};

  const isAuthEndpoint = config.url?.includes('/auth/');
  const viewAsToken = sessionStorage.getItem(VIEW_AS_TOKEN_KEY);
  if (viewAsToken && !config.url?.includes('/admin/view-as')) {
    config.headers.Authorization = `Bearer ${viewAsToken}`;
    return config;
  }
  if (!isAuthEndpoint) {
    const token = localStorage.getItem('authToken');
    if (token) {
      const raw = token.startsWith('Bearer ') ? token.slice(7) : token;
      config.headers.Authorization = `Bearer ${raw}`;
    }
  }

  const activeTenantId = sessionStorage.getItem('activeTenantId');
  if (activeTenantId) {
    config.headers['X-Active-Tenant'] = activeTenantId;
  }

  const activeFirmId = localStorage.getItem('active_firm_id');
  if (activeFirmId) {
    config.headers['x-firm-id'] = activeFirmId;
  }

  return config;
});

// Optional callback so the React AuthContext can sync state after a
// background token refresh without requiring a page reload.
let onTokenRefreshed = null;
export function setTokenRefreshedCallback(fn) {
  onTokenRefreshed = fn;
}

// Track whether a refresh is already in flight so concurrent 401s don't
// each fire their own refresh request.
let refreshPromise = null;

api.interceptors.response.use(
  res => res,
  async error => {
    const original = error.config;

    // A View As token is never refreshed: when it expires (or is refused),
    // View As ends and the super admin goes back to the admin page.
    if (error.response?.status === 401 && sessionStorage.getItem(VIEW_AS_TOKEN_KEY)
        && !original?.url?.includes('/admin/view-as')) {
      clearViewAs();
      sessionStorage.setItem('authMode', 'admin');
      window.location.href = '/super-admin';
      return Promise.reject(error);
    }

    // Only attempt refresh on 401, once per request, and not on the refresh
    // endpoint itself (avoids infinite loop).
    if (
      error.response?.status === 401 &&
      !original._retried &&
      !original.url?.includes('/auth/refresh') &&
      !original.url?.includes('/auth/login')
    ) {
      original._retried = true;

      if (!refreshPromise) {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          forceLogout();
          return Promise.reject(error);
        }

        refreshPromise = axios
          .post(`${BASE_URL}/auth/refresh`, { refreshToken })
          .then(r => {
            const { token, refreshToken: newRefresh, user } = r.data;
            localStorage.setItem('authToken', token);
            if (newRefresh) localStorage.setItem('refreshToken', newRefresh);
            if (user) localStorage.setItem('authUser', JSON.stringify(user));
            setAuthToken(token);
            if (onTokenRefreshed) onTokenRefreshed(token, user);
            return token;
          })
          .catch(() => {
            forceLogout();
            return null;
          })
          .finally(() => {
            refreshPromise = null;
          });
      }

      const newToken = await refreshPromise;
      if (!newToken) return Promise.reject(error);

      original.headers = original.headers || {};
      original.headers.Authorization = `Bearer ${newToken}`;
      return api(original);
    }

    return Promise.reject(error);
  }
);

function forceLogout() {
  localStorage.removeItem('authToken');
  localStorage.removeItem('authUser');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('active_firm_id');
  localStorage.removeItem('active_firm_name');
  sessionStorage.removeItem('authMode');
  sessionStorage.removeItem('activeTenantId');
  sessionStorage.removeItem('activeTenantName');
  clearViewAs();
  delete api.defaults.headers.common.Authorization;
  window.location.href = '/login';
}

export default api;
